import { Application } from '@platform/client';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';
import { EYReferralSelection } from '../program/ey/EYReferralSelection';
import { EYConfirmAndSubmit } from '../final-details/ey/EYConfirmAndSubmit';

enum EYApplicationSteps {
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',

  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
  CONFIRM_AND_SUBMIT = 'CONFIRM_AND_SUBMIT',
}

const EYApplicationStep: Record<EYApplicationSteps, Step> = {
  [EYApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/',
  },
  [EYApplicationSteps.LOCATION_SELECTION]: {
    progress: 135,
    component: LocationSelection,
    url: '/location-selection',
  },
  [EYApplicationSteps.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [EYApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 170,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [EYApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [EYApplicationSteps.SKILLS_SELECTION]: {
    progress: 235,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [EYApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 275,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [EYApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 340,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [EYApplicationSteps.REFERRAL_SELECTION]: {
    progress: 385,
    component: EYReferralSelection,
    url: '/referral-selection',
  },
  [EYApplicationSteps.CONFIRM_AND_SUBMIT]: {
    progress: 400,
    component: EYConfirmAndSubmit,
    url: '/confirm-and-submit',
  },
} as const;

export const EYApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(EYApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return EYApplicationStep[step as EYApplicationSteps].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = EYApplicationStep[nextStep as EYApplicationSteps].url;
    navigate(nextUrl);
  };

  const goToPreviousStep = () => {
    const steps = Object.values(EYApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => EYApplicationStep[step as EYApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=ey';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = EYApplicationStep[previousStep as EYApplicationSteps].url;
    navigate(previousUrl);
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applyCompleted) {
    return <ApplicationApproved
      cohortKickoffDate={applicationData.data.cohortKickoffDate}
      clientUrlParam={applicationData.data.clientUrlParam}
    />;
  }

  return (
    <Switch>
      {Object.entries(EYApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
